import { renderField } from "@modyra/plain";
import { MDY_LAYOUT_CLASSES, layoutNodeAttributes, layoutSlotStyle } from "@modyra/widgets";
/**
 * What the mounted structure is made of.
 *
 * Remounting is only worth doing when this changes: the schema, the arrangement, the number of rows
 * in each repeater, and the mock modes (which the per-field selector reflects). Field *values* are
 * deliberately absent — a mounted control owns its own value, and remounting on every keystroke is
 * exactly what this signature exists to prevent.
 */
export function previewStructureSignature(project, layout, handleFor, mockConfig) {
    const lengths = {};
    const visit = (node, path) => {
        if (node.node === "field")
            return;
        if (node.node === "group") {
            for (const child of node.children)
                visit(child, path ? `${path}.${child.name}` : child.name);
            return;
        }
        const length = rowCount(handleFor(path));
        lengths[path] = length;
        for (let index = 0; index < length; index += 1)
            visit(node.item, `${path}.${index}`);
    };
    visit(project.schema, project.schema.node === "group" ? "" : project.schema.name);
    return JSON.stringify({ schema: project.schema, layout, lengths, mockConfig });
}
/** Reading `length()` here is what subscribes the caller's effect to a push or a remove. */
function rowCount(handle) {
    const length = handle?.length;
    return typeof length === "function" ? Number(length.call(handle)) || 0 : 0;
}
/** The keys a keyed collection currently has, in the order its handle reports them. */
function rowKeys(handle) {
    const keys = handle?.keys;
    return typeof keys === "function" ? keys.call(handle) ?? [] : [];
}
/**
 * Renders the project's fields into `container` as real controls, arranged the way the canvas
 * arranges them. Returns a disposer that tears every mounted control down.
 */
export function mountPreviewFields(container, project, options) {
    container.replaceChildren();
    // Preview is a form root like the other two renderers', named the same way. That is what lets the
    // foundation treat it as a layout container, so the arrangement follows the panel's own width.
    container.classList.add("mdy-dynamic-form");
    const disposers = [];
    if (project.schema.node !== "group")
        return { dispose: () => container.replaceChildren() };
    const element = (tag, className) => {
        const node = document.createElement(tag);
        if (className)
            node.className = className;
        return node;
    };
    const mountField = (target, node, path) => {
        const handle = options.handleFor(path);
        const field = options.fieldFor(node, path);
        if (!handle || !field)
            return;
        const host = element("div", "preview-field-host");
        host.dataset.previewNode = path;
        target.append(host);
        disposers.push(renderField(host, field, handle, options.reactivity));
        mountMockMode(target, node);
    };
    /** Studio chrome, not the form's: which outcome the server validator's mock will produce. */
    const mountMockMode = (target, node) => {
        const validator = node.serverValidator;
        if (!validator)
            return;
        const config = options.mockConfig[validator.implementationRef];
        const mode = config?.forceNetworkFailure ? "network" : config?.forceError ? "error" : "success";
        const label = element("label", "preview-mock-mode");
        label.append("Server mock");
        const select = document.createElement("select");
        select.dataset.previewMockMode = validator.implementationRef;
        for (const [value, text] of [["success", "Succeeds"], ["error", "Fails"], ["network", "Network failure"]]) {
            const option = document.createElement("option");
            option.value = value;
            option.textContent = text;
            option.selected = value === mode;
            select.append(option);
        }
        label.append(select);
        target.append(label);
    };
    const mountGroup = (target, node, path) => {
        const section = element("fieldset", MDY_LAYOUT_CLASSES.section);
        const legend = element("legend", MDY_LAYOUT_CLASSES.sectionLabel);
        legend.textContent = node.label || node.name;
        section.append(legend);
        for (const child of node.children)
            mountNode(section, child, `${path}.${child.name}`);
        target.append(section);
    };
    /**
     * A collection, drawn row by row from the handle rather than from the project.
     *
     * Which rows exist is the running form's answer, not the author's: a positional collection is
     * asked for its length and a keyed one for its keys, and the preview draws what it is told.
     */
    const mountCollection = (target, node, path) => {
        const handle = options.handleFor(path);
        const keyed = node.node === "record";
        const rows = keyed
            ? rowKeys(handle)
            : Array.from({ length: rowCount(handle) }, (_, index) => String(index));
        const wrapper = element("div", "preview-array");
        const label = element("div", "preview-array-label");
        label.textContent = `${node.label || node.name} (${rows.length})`;
        wrapper.append(label);
        for (const rowKey of rows) {
            const rowPath = `${path}.${rowKey}`;
            const row = element("div", "preview-array-row");
            if (keyed) {
                const key = element("span", "preview-array-key");
                key.textContent = rowKey;
                row.append(key);
            }
            if (node.item.node === "group") {
                for (const child of node.item.children)
                    mountNode(row, child, `${rowPath}.${child.name}`);
            }
            else {
                mountNode(row, node.item, rowPath);
            }
            const remove = element("button");
            remove.type = "button";
            remove.textContent = "Remove";
            remove.dataset.previewArrayRemove = path;
            remove.dataset.previewArrayIndex = rowKey;
            row.append(remove);
            wrapper.append(row);
        }
        const push = element("button");
        push.type = "button";
        push.textContent = "+ Add row";
        push.dataset.previewArrayPush = path;
        wrapper.append(push);
        target.append(wrapper);
    };
    const mountNode = (target, node, path) => {
        if (node.node === "field")
            mountField(target, node, path);
        else if (node.node === "group")
            mountGroup(target, node, path);
        else
            mountCollection(target, node, path);
    };
    mountArrangement(container, project.schema.children, project.presentation.layout ?? [], element, mountNode);
    return {
        dispose: () => {
            for (const off of disposers.reverse())
                off();
            container.replaceChildren();
        },
    };
}
/**
 * Places the root's children, honouring the column rows the project's layout declares.
 *
 * The row is emitted at its first member's position and its members are not emitted again — the
 * same splice rule `@modyra/plain` applies, so what you preview is arranged the way what ships is.
 */
function mountArrangement(container, rootChildren, layout, element, mountNode) {
    const columnRows = layout.filter((node) => node.kind === "columns");
    if (!columnRows.length) {
        for (const child of rootChildren)
            mountNode(container, child, child.name);
        return;
    }
    const byId = new Map(rootChildren.map((child) => [child.id, child]));
    const rowFor = new Map();
    const claimed = new Set();
    for (const row of columnRows) {
        const members = row.columns.flat().flatMap((child) => ("nodeId" in child ? [child.nodeId] : []));
        const anchorId = members.find((id) => byId.has(id) && !claimed.has(id));
        if (anchorId === undefined)
            continue;
        rowFor.set(anchorId, row);
        for (const id of members)
            claimed.add(id);
    }
    for (const child of rootChildren) {
        const row = rowFor.get(child.id);
        if (row) {
            const grid = element("div", MDY_LAYOUT_CLASSES.columns);
            // The row's own attributes, from the same function the two shipping renderers call. Writing
            // the declared count by hand meant Preview showed one arrangement at every size while the form
            // it previews changed at three — the per-breakpoint counts were never emitted here at all.
            for (const [property, value] of Object.entries(layoutNodeAttributes(row).style)) {
                grid.style.setProperty(property, value);
            }
            for (const column of row.columns) {
                const cell = element("div", MDY_LAYOUT_CLASSES.column);
                // The column is the grid item, so a slot's placement is applied to it — the same reading as
                // `@modyra/plain` and `<mdy-dynamic-form>`. First slot with something to say wins.
                const placed = column.find((slot) => "nodeId" in slot && slot.at !== undefined);
                if (placed && "nodeId" in placed) {
                    for (const [property, value] of Object.entries(layoutSlotStyle(placed.at))) {
                        cell.style.setProperty(property, value);
                    }
                }
                for (const slot of column) {
                    const node = "nodeId" in slot ? byId.get(slot.nodeId) : undefined;
                    if (node)
                        mountNode(cell, node, node.name);
                }
                grid.append(cell);
            }
            container.append(grid);
            continue;
        }
        if (!claimed.has(child.id))
            mountNode(container, child, child.name);
    }
}
